// Production — the run pipeline the manufacturer drives, as a clear supply-chain timeline. Each run
// is a card with a step tracker (Booked → Ingredients in → In production → Produced → Shipped) and a
// single obvious next action. "Produced" records made/sent/kept (kept auto-posts to At-Mfr). Shared
// by the owner Production view and the scoped canner view.
import { useEffect, useMemo, useRef, useState } from "react";
import type { Run, RunStatus } from "@shared/types.ts";
import { trackingUrl } from "@shared/engine.ts";
import { Badge, EmptyState, Modal, LabeledInput } from "./ui.tsx";
import { fmtDate, num } from "../lib/format.ts";

type CompShipment = { carrier?: string; trackingNumber?: string; shippedDate?: string; expectedDelivery?: string; note?: string };

type CItem = { skuId: string; made: number; sent: number; kept: number };
export type SkuMeta = { name: string; flavour?: string; strength?: string; imageUrl?: string; productType?: string };
export type RecipeRow = { ingredient: string; lbsPerBbl: number; lbsPerCan: number };

// A stable-ish colour per flavour for the little product graphic (used when there's no image).
const FLAVOUR_COLORS: [RegExp, string][] = [
  [/mint|lime|mojito/i, "#0ea5e9"],
  [/pi[nñ]a|pineapple|colada/i, "#eab308"],
  [/rasp|berry|cherry|lemonade/i, "#e11d48"],
  [/coconut/i, "#64748b"],
  [/mix|variety/i, "#8b5cf6"],
  [/orange|mango|citrus/i, "#f97316"],
  [/cola|spirit|whisk|rum|gin/i, "#92400e"],
];
function flavourColor(s: string): string {
  for (const [re, c] of FLAVOUR_COLORS) if (re.test(s)) return c;
  // deterministic fallback from the string
  let h = 0; for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) & 0xffff;
  return `hsl(${h % 360} 55% 45%)`;
}
// A product-format icon (can / bottle / gummy pack) drawn white on the flavour swatch.
function ProductIcon({ type, className = "w-5 h-5" }: { type?: string; className?: string }) {
  const t = type === "bottle" ? "bottle" : type === "gummy" ? "gummy" : "can";
  const common = { viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 1.7, strokeLinecap: "round" as const, strokeLinejoin: "round" as const, className };
  if (t === "bottle") return (
    <svg {...common}><path d="M10 2.5h4v2.6l1.5 2.4c.33.53.5 1.14.5 1.76V19a2 2 0 0 1-2 2h-4a2 2 0 0 1-2-2V9.26c0-.62.17-1.23.5-1.76L10 5.1z" /><path d="M9.5 13.5h5" /></svg>
  );
  if (t === "gummy") return (
    <svg {...common}><path d="M7 7.5h10l-.8 11a1.7 1.7 0 0 1-1.7 1.6H9.5a1.7 1.7 0 0 1-1.7-1.6L7 7.5z" /><path d="M8.3 7.5 9.4 4h5.2l1.1 3.5" /><path d="M10 4h4" /></svg>
  );
  return ( // can
    <svg {...common}><rect x="7.5" y="3.5" width="9" height="17" rx="2.4" /><path d="M8 7h8" /><path d="M8 17h8" /></svg>
  );
}

// One product in a run — small image (or colour swatch) + flavour/strength + quantity.
function SkuChip({ meta, qty, unit, onClick }: { meta: SkuMeta; qty: number; unit: string; onClick?: () => void }) {
  const label = meta.flavour || meta.name;
  // Fall back to the colour swatch if the image is missing OR fails to load (broken URL, hotlink block…).
  const [imgOk, setImgOk] = useState(true);
  useEffect(() => setImgOk(true), [meta.imageUrl]);
  const showImg = !!meta.imageUrl && imgOk;
  return (
    <button type="button" onClick={onClick} disabled={!onClick}
      className={`w-full text-left flex items-center gap-2.5 rounded-lg border border-edge/70 bg-paper px-2.5 py-1.5 ${onClick ? "hover:border-brand/40 hover:bg-brandSoft/40 transition" : "cursor-default"}`}>
      {showImg
        ? <img src={meta.imageUrl} alt="" onError={() => setImgOk(false)} className="w-9 h-9 rounded-md object-cover shrink-0 border border-edge/60 bg-white" />
        : <div className="w-9 h-9 rounded-md grid place-items-center text-white shrink-0" style={{ background: flavourColor(label) }}><ProductIcon type={meta.productType} /></div>}
      <div className="min-w-0">
        <div className="text-sm font-medium text-ink leading-tight truncate">{label}</div>
        {meta.strength && <div className="text-[11px] text-muted leading-tight">{meta.strength}{onClick ? " · recipe ›" : ""}</div>}
      </div>
      <div className="ml-auto text-right shrink-0">
        <div className="text-sm font-semibold tabular-nums leading-tight">{num(qty)}</div>
        <div className="text-[10px] text-muted leading-tight">{unit}s</div>
      </div>
    </button>
  );
}

// 6-step pipeline: Date, Ingredients in, Packaging (a checklist of components), In production, Produced,
// Shipped. Packaging is one step; the components are confirmed via a non-linear checklist beneath it.
function stageOf(run: Run): number {
  switch (run.status) {
    case "Delivered": return 5;
    case "Waiting Pickup": return 4;
    case "In Production": return 3;
    case "Ingredients Received": return 2; // packaging phase (ingredients done)
    default: return 0; // Planned / Booked / Ingredients Ordered
  }
}

// Short stage names for the "jump to a booked production" navigator, indexed by stageOf().
const STAGE_SHORT = ["Booked", "Ingredients", "Packaging", "In production", "Produced", "Shipped"];

type RunPatch = { scheduledDate?: string; readyDate?: string; poNumber?: string; batchNumber?: string; notes?: string; packagingReceived?: boolean; componentsReceived?: string[]; componentShipment?: { name: string; shipment: CompShipment }; delay?: { toDate: string; reason?: string } };
type ScheduleAction = "propose" | "confirm";

function daysBetween(from?: string, to?: string): number {
  if (!from || !to) return 0;
  return Math.round((new Date(`${to}T00:00:00`).getTime() - new Date(`${from}T00:00:00`).getTime()) / 86400000);
}

export function ProductionPanel({ runs, skuName, skuMeta, skuRecipe, unit, busy, role = "owner", focusRunId, ingredientProgress, ingredientEta, containerWord, components, onSetStatus, onComplete, onUpdate, onSchedule, onNudge }: {
  runs: Run[];
  focusRunId?: string | null; // scroll to + highlight this run (e.g. opened from a notification)
  skuName: (id: string) => string;
  skuMeta?: (id: string) => SkuMeta; // richer per-SKU display (flavour, strength, image) for the run card
  skuRecipe?: (id: string) => RecipeRow[] | null; // recipe (lbs/BBL) shown when a SKU is tapped
  unit: string;
  busy: boolean;
  role?: "owner" | "mfr"; // who's viewing — drives the date handshake ("your turn" logic + labels)
  ingredientProgress: (run: Run) => { have: number; total: number; missing: string[] } | null; // of the run's ingredients, how many are covered + which are still short
  ingredientEta?: (run: Run) => string | null; // latest expected/received date across the run's ingredient order (the "all-in" date)
  containerWord: (run: Run) => string; // "Cans" | "Bottles" | "Gummy packs" for this run's product type
  components: (run: Run) => string[]; // ordered packaging-component steps for this run's product type
  onSetStatus: (runId: string, status: RunStatus) => Promise<void> | void;
  onComplete: (runId: string, items: CItem[], notes: string) => Promise<void> | void;
  onUpdate: (runId: string, patch: RunPatch) => Promise<void> | void;
  onSchedule: (runId: string, action: ScheduleAction, date?: string, note?: string) => Promise<void> | void;
  // Brand-only: send a manual reminder to the manufacturer (emails them). Absent = no reminder button.
  onNudge?: (runId: string, title: string, message: string) => Promise<{ emailed: boolean; recipients: number } | void>;
}) {
  const [complete, setComplete] = useState<Run | null>(null);
  const [details, setDetails] = useState<Run | null>(null);
  const [nudgeRun, setNudgeRun] = useState<Run | null>(null);
  const [delayRun, setDelayRun] = useState<Run | null>(null);
  const [scheduleRun, setScheduleRun] = useState<Run | null>(null);
  const [recipeSku, setRecipeSku] = useState<string | null>(null);
  // Internal "jump to" focus set by the booked-productions navigator (separate from the external
  // focusRunId coming from a notification). Clears after a few seconds so re-clicking re-scrolls.
  const [jumpId, setJumpId] = useState<string | null>(null);
  const jumpTo = (id: string) => { setJumpId(id); window.setTimeout(() => setJumpId((c) => (c === id ? null : c)), 4000); };

  const { active, shipped } = useMemo(() => {
    const live = runs.filter((r) => r.status !== "Cancelled");
    return {
      active: live.filter((r) => r.status !== "Delivered").sort((a, b) => (a.scheduledDate < b.scheduledDate ? -1 : 1)),
      shipped: live.filter((r) => r.status === "Delivered").sort((a, b) => (a.scheduledDate < b.scheduledDate ? 1 : -1)),
    };
  }, [runs]);

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-sm font-semibold">Production</h2>
        <p className="text-muted text-xs mt-0.5">Each production run, start to finish. Move it along as you go — start production, then record what you made and how much you're sending vs keeping.</p>
      </div>

      {active.length === 0 && shipped.length === 0 ? (
        <div className="card"><EmptyState title="No production runs yet" sub="Booked productions will appear here for you to work through." /></div>
      ) : (
        <div className="space-y-3">
          {/* Jump-to navigator — every booked production at a glance; tap one to scroll to it. */}
          {active.length > 1 && (
            <div className="card p-3">
              <div className="text-[11px] uppercase tracking-wide text-muted mb-2">Booked productions ({active.length}) · tap to jump</div>
              <div className="flex flex-wrap gap-2">
                {active.map((r) => {
                  const st = stageOf(r);
                  const qty = r.items.reduce((a, i) => a + (i.cans || 0), 0);
                  return (
                    <button key={r.id} onClick={() => jumpTo(r.id)}
                      className={`text-left rounded-xl border px-3 py-1.5 transition ${jumpId === r.id ? "border-brand bg-brand/[0.06] ring-1 ring-brand" : "border-edge/70 bg-paper/60 hover:border-edge hover:bg-paper"}`}>
                      <div className="text-sm font-semibold text-ink whitespace-nowrap">{fmtDate(r.scheduledDate)}</div>
                      <div className="text-[11px] text-muted whitespace-nowrap">{num(qty)} {unit}s · {STAGE_SHORT[st]}</div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          {active.map((r) => <RunCard key={r.id} run={r} role={role} skuName={skuName} skuMeta={skuMeta} unit={unit} busy={busy} onSetStatus={onSetStatus} onRecord={() => setComplete(r)} onEdit={() => setDetails(r)} onDelay={() => setDelayRun(r)} ing={ingredientProgress(r)} eta={ingredientEta?.(r) ?? null} onUpdate={onUpdate} onSchedule={onSchedule} onCounter={() => setScheduleRun(r)} onNudge={onNudge ? () => setNudgeRun(r) : undefined} onSkuClick={skuRecipe ? setRecipeSku : undefined} focus={(!!focusRunId && r.id === focusRunId) || jumpId === r.id} containerWord={containerWord(r)} comps={components(r)} />)}
        </div>
      )}

      {shipped.length > 0 && (
        <details className="card" open={!!focusRunId && shipped.some((r) => r.id === focusRunId)}>
          <summary className="cursor-pointer text-sm font-medium text-muted">Shipped &amp; complete ({shipped.length})</summary>
          <div className="space-y-3 mt-3">
            {shipped.map((r) => <RunCard key={r.id} run={r} role={role} skuName={skuName} skuMeta={skuMeta} unit={unit} busy={busy} onSetStatus={onSetStatus} onRecord={() => setComplete(r)} onEdit={() => setDetails(r)} onDelay={() => setDelayRun(r)} ing={ingredientProgress(r)} eta={ingredientEta?.(r) ?? null} onUpdate={onUpdate} onSchedule={onSchedule} onCounter={() => setScheduleRun(r)} onSkuClick={skuRecipe ? setRecipeSku : undefined} focus={!!focusRunId && r.id === focusRunId} containerWord={containerWord(r)} comps={components(r)} />)}
          </div>
        </details>
      )}

      {complete && (
        <CompletionModal run={complete} skuName={skuName} unit={unit} busy={busy}
          onClose={() => setComplete(null)}
          onSave={async (items, notes) => { await onComplete(complete.id, items, notes); setComplete(null); }} />
      )}
      {details && (
        <RunDetailsModal run={details} busy={busy} onClose={() => setDetails(null)}
          onSave={async (patch) => { await onUpdate(details.id, patch); setDetails(null); }} />
      )}
      {delayRun && (
        <DelayModal run={delayRun} busy={busy} onClose={() => setDelayRun(null)}
          onSave={async (toDate, reason) => { await onUpdate(delayRun.id, { delay: { toDate, reason } }); setDelayRun(null); }} />
      )}
      {scheduleRun && (
        <ScheduleModal run={scheduleRun} role={role} busy={busy} onClose={() => setScheduleRun(null)}
          onSave={async (date, note) => { await onSchedule(scheduleRun.id, "propose", date, note); setScheduleRun(null); }} />
      )}
      {recipeSku && skuRecipe && (
        <RecipeModal name={skuName(recipeSku)} rows={skuRecipe(recipeSku)} onClose={() => setRecipeSku(null)} />
      )}
      {nudgeRun && onNudge && (
        <NudgeModal run={nudgeRun} comps={components(nudgeRun)} ing={ingredientProgress(nudgeRun)} busy={busy}
          onClose={() => setNudgeRun(null)}
          onSend={(title, message) => onNudge(nudgeRun.id, title, message)} />
      )}
    </div>
  );
}

// Recipe for one SKU, in lbs per barrel (the number production works in) + lbs per unit.
function RecipeModal({ name, rows, onClose }: { name: string; rows: RecipeRow[] | null; onClose: () => void }) {
  const list = rows || [];
  const totalBbl = list.reduce((a, r) => a + (r.lbsPerBbl || 0), 0);
  return (
    <Modal title={`Recipe · ${name}`} onClose={onClose}>
      <div className="space-y-3">
        <p className="text-xs text-muted">Per-ingredient dose. <span className="font-medium text-ink">lbs/BBL</span> is the working figure (lbs per US beer barrel ≈ 330 cans).</p>
        {list.length === 0 ? (
          <div className="text-sm text-muted text-center py-6">No recipe defined for this SKU yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[360px]">
              <thead><tr><th className="th">Ingredient</th><th className="th text-right">lbs / BBL</th><th className="th text-right">lbs / can</th></tr></thead>
              <tbody>
                {list.map((r) => (
                  <tr key={r.ingredient}>
                    <td className="td font-medium">{r.ingredient}</td>
                    <td className="td text-right tabular-nums font-semibold">{r.lbsPerBbl.toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                    <td className="td text-right tabular-nums text-muted">{r.lbsPerCan.toLocaleString(undefined, { maximumFractionDigits: 4 })}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot><tr>
                <td className="td font-semibold">Total</td>
                <td className="td text-right font-semibold tabular-nums">{totalBbl.toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                <td className="td"></td>
              </tr></tfoot>
            </table>
          </div>
        )}
      </div>
    </Modal>
  );
}

function RunCard({ run, role, skuName, skuMeta, unit, busy, onSetStatus, onRecord, onEdit, onDelay, onUpdate, onSchedule, onCounter, onNudge, onSkuClick, ing, eta, focus, containerWord, comps }: {
  run: Run; role: "owner" | "mfr"; skuName: (id: string) => string; skuMeta?: (id: string) => SkuMeta; unit: string; busy: boolean;
  onSetStatus: (runId: string, status: RunStatus) => Promise<void> | void; onRecord: () => void; onEdit: () => void; onDelay: () => void;
  onUpdate: (runId: string, patch: RunPatch) => Promise<void> | void;
  onSchedule: (runId: string, action: ScheduleAction, date?: string, note?: string) => Promise<void> | void;
  onCounter: () => void;
  onNudge?: () => void; // brand-only: open the "remind manufacturer" composer
  onSkuClick?: (skuId: string) => void;
  ing: { have: number; total: number; missing: string[] } | null;
  eta: string | null; // latest ingredient arrival date for the run
  focus?: boolean;    // opened from a notification — scroll to it and ring it
  containerWord: string; // "Cans" | "Bottles" | "Gummy packs"
  comps: string[];    // ordered packaging-component steps (spirits: Bottles/Labels/Corks/Caps)
}) {
  const cardRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (focus && cardRef.current) cardRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [focus]);
  const [trackName, setTrackName] = useState<string | null>(null); // component whose tracking is being edited
  // Packaging components (from the product type's `received` build parts) — confirmed via a checklist,
  // any order. Falls back to the legacy all-or-nothing flag for old runs.
  const compList = comps.length ? comps : [containerWord];
  const K = compList.length;
  const receivedSet = new Set(run.componentsReceived && run.componentsReceived.length ? run.componentsReceived : (run.packagingReceived ? compList : []));
  const received = compList.filter((cn) => receivedSet.has(cn));
  const rc = received.length;
  const allPackagingIn = rc >= K;
  const toggleComp = (cn: string) => onUpdate(run.id, { componentsReceived: receivedSet.has(cn) ? received.filter((x) => x !== cn) : [...received, cn] });
  const stage = stageOf(run);
  const steps = ["Date", "Ingredients in", K > 1 ? "Packaging" : `${compList[0]} in`, "In production", "Produced", "Shipped to us"];
  // Date handshake state. Legacy runs with no schedule are treated as already agreed.
  const sched = run.schedule;
  const dateConfirmed = !sched || sched.state === "confirmed";
  const awaiting = sched?.awaiting;              // whose turn while proposed
  const myTurn = !dateConfirmed && awaiting === role;
  const them = role === "owner" ? "the manufacturer" : "Little Rick";
  const planned = run.items.reduce((a, i) => a + i.cans, 0);
  const c = run.completion;
  const delays = run.delays || [];
  const totalDelay = daysBetween(run.originalReadyDate, run.readyDate);
  const ingPct = ing && ing.total ? Math.round((100 * ing.have) / ing.total) : null;
  return (
    <div ref={cardRef} className={`card space-y-3 transition-shadow ${focus ? "ring-2 ring-brand ring-offset-2" : ""}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="font-semibold text-sm">Start {fmtDate(run.scheduledDate)}{run.readyDate ? <span className="text-muted font-normal"> · Ready ~{fmtDate(run.readyDate)}</span> : ""}</div>
          <div className="text-xs text-muted">{num(c ? c.items.reduce((a, i) => a + i.made, 0) : planned)} {unit}s · {run.items.length} SKU{run.items.length !== 1 ? "s" : ""}{run.poNumber ? ` · PO ${run.poNumber}` : ""}{run.batchNumber ? ` · Batch ${run.batchNumber}` : ""}</div>
          {run.notes && <div className="text-[11px] text-muted italic mt-0.5">“{run.notes}”</div>}
        </div>
        <div className="flex items-center gap-2">
          {delays.length > 0 && totalDelay > 0 && <Badge tone="danger">Delayed +{totalDelay}d</Badge>}
          <Badge tone={stage === 5 ? "ok" : stage === 4 ? "brand" : "warn"}>{run.status}</Badge>
          {onNudge && role === "owner" && run.status !== "Delivered" && (
            <button className="btn-sm border border-brand/40 text-brand hover:bg-brand/5 whitespace-nowrap" disabled={busy} onClick={onNudge}>Remind mfr</button>
          )}
          <button className="btn-sm border border-edge hover:bg-paper whitespace-nowrap" disabled={busy} onClick={onEdit}>Edit dates</button>
        </div>
      </div>

      {delays.length > 0 && (
        <div className="rounded-lg border border-danger/30 bg-danger/5 p-2.5 text-xs space-y-1">
          <div className="font-medium text-danger">Delays{run.originalReadyDate ? ` · originally ready ~${fmtDate(run.originalReadyDate)}` : ""}</div>
          {delays.map((d, i) => (
            <div key={i} className="text-muted flex flex-wrap gap-x-2">
              <span className="text-ink">→ {fmtDate(d.toDate)}</span>
              {d.days ? <span className="text-danger">(+{d.days}d)</span> : null}
              {d.reason ? <span>· {d.reason}</span> : null}
            </div>
          ))}
        </div>
      )}

      <Timeline stage={stage} steps={steps} ingPct={ingPct} ingredientDate={eta} packPct={stage === 2 && K > 1 ? Math.round((100 * rc) / K) : null} />
      {eta && <div className="hidden sm:block text-[11px] text-muted text-center mt-1">Ingredients {stage > 1 ? "all in by" : "due by"} <span className="text-ink font-medium">{fmtDate(eta)}</span></div>}

      {!dateConfirmed && (
        <div className="rounded-lg border border-warn/40 bg-warn/5 p-3 space-y-2">
          <div className="flex items-center justify-between gap-2">
            <div className="text-sm font-medium text-warn">Start date {myTurn ? "— your confirmation needed" : `— awaiting ${awaiting === "owner" ? "Little Rick" : "the manufacturer"}`}</div>
            <Badge tone="warn">Proposed</Badge>
          </div>
          <div className="text-sm">Proposed start: <span className="font-semibold">{fmtDate(run.scheduledDate)}</span></div>
          {sched && sched.thread.length > 1 && (
            <details className="text-[11px] text-muted">
              <summary className="cursor-pointer">History ({sched.thread.length})</summary>
              <div className="mt-1 space-y-0.5">
                {sched.thread.map((t, i) => (
                  <div key={i}><span className="text-ink">{t.by === "owner" ? "Little Rick" : "Manufacturer"}</span> {t.kind === "confirm" ? "confirmed" : t.kind === "propose" ? "proposed" : "countered"} {fmtDate(t.date)}{t.note ? ` · ${t.note}` : ""}</div>
                ))}
              </div>
            </details>
          )}
          {myTurn ? (
            <div className="flex flex-col sm:flex-row gap-2 [&>button]:w-full sm:[&>button]:w-auto [&>button]:whitespace-nowrap">
              <button className="btn-primary btn-sm" disabled={busy} onClick={() => onSchedule(run.id, "confirm")}>Confirm {fmtDate(run.scheduledDate)}</button>
              <button className="btn-sm border border-edge hover:bg-paper" disabled={busy} onClick={onCounter}>Suggest another date</button>
            </div>
          ) : (
            <div className="text-xs text-muted">Waiting on {them} to respond.</div>
          )}
        </div>
      )}

      {dateConfirmed && ing && stage === 0 && (
        <div className="space-y-1">
          <div className="flex justify-between text-[11px]">
            <span className="text-muted">Ingredients ready (in stock or received)</span>
            <span className="font-medium">{ing.have} of {ing.total} · {ingPct}%</span>
          </div>
          <div className="h-1.5 rounded-full bg-edge/50 overflow-hidden">
            <div className={`h-full transition-all ${ingPct === 100 ? "bg-ok" : "bg-warn"}`} style={{ width: `${ingPct || 0}%` }} />
          </div>
          {ing.missing.length > 0
            ? <div className="text-[11px] text-warn">Still waiting on: <span className="text-ink">{ing.missing.join(", ")}</span></div>
            : <div className="text-[11px] text-ok">All ingredients ready ✓</div>}
          {eta && ing.missing.length > 0 && <div className="text-[11px] text-muted">All ingredients due by <span className="text-ink font-medium">{fmtDate(eta)}</span></div>}
        </div>
      )}

      {run.status === "Ingredients Received" && (
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wide text-muted">Packaging</span>
            <span className="text-[11px] font-medium">{rc} of {K} received</span>
          </div>
          <div className="rounded-lg border border-edge/70 divide-y divide-edge/60 overflow-hidden">
            {compList.map((cn) => {
              const on = receivedSet.has(cn);
              const ship = run.componentShipments?.[cn];
              const turl = ship ? trackingUrl(ship.carrier, ship.trackingNumber) : null;
              const hasTracking = !!(ship && (ship.shippedDate || ship.expectedDelivery || ship.carrier || ship.trackingNumber));
              return (
                <div key={cn} className={`flex items-center gap-2.5 px-3 py-2 ${on ? "bg-ok/5" : ""}`}>
                  <span className={`w-5 h-5 rounded-full grid place-items-center text-[11px] shrink-0 ${on ? "bg-ok text-white" : "border border-edge text-muted"}`}>{on ? "✓" : ""}</span>
                  <div className="flex-1 min-w-0">
                    <div className={`text-sm ${on ? "text-ink font-medium" : "text-ink"}`}>{cn}</div>
                    {hasTracking && (
                      <div className="text-[11px] text-muted">
                        {ship!.shippedDate ? `Shipped ${fmtDate(ship!.shippedDate)}` : ""}{ship!.shippedDate && ship!.expectedDelivery ? " · " : ""}{ship!.expectedDelivery ? `ETA ${fmtDate(ship!.expectedDelivery)}` : ""}{ship!.carrier ? ` · ${ship!.carrier}` : ""}
                        {turl && <> · <a href={turl} target="_blank" rel="noreferrer" className="text-brand hover:underline">track</a></>}
                      </div>
                    )}
                  </div>
                  {role === "owner" && <button type="button" className="text-[11px] text-brand hover:underline shrink-0" onClick={() => setTrackName(cn)}>{hasTracking ? "Tracking" : "+ Tracking"}</button>}
                  {on ? (
                    <span className="flex items-center gap-2 shrink-0">
                      <Badge tone="ok">Received</Badge>
                      <button type="button" className="text-[11px] text-muted hover:text-danger" disabled={busy} onClick={() => toggleComp(cn)}>Undo</button>
                    </span>
                  ) : (
                    <button type="button" className="btn-sm bg-brand text-white hover:bg-brand/90 shrink-0 whitespace-nowrap" disabled={busy} onClick={() => toggleComp(cn)}>Mark received →</button>
                  )}
                </div>
              );
            })}
          </div>
          <p className="text-[11px] text-muted">{role === "owner" ? "Add tracking so the manufacturer sees each delivery coming; they mark it received on arrival." : "Mark each packaging item as it arrives. Start production once all are received."}</p>
          {trackName && (
            <ComponentTrackingModal name={trackName} shipment={run.componentShipments?.[trackName] || {}} busy={busy}
              onClose={() => setTrackName(null)}
              onSave={async (shipment) => { await onUpdate(run.id, { componentShipment: { name: trackName, shipment } }); setTrackName(null); }} />
          )}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
        {run.items.map((i) => <SkuChip key={i.skuId} meta={skuMeta ? skuMeta(i.skuId) : { name: skuName(i.skuId) }} qty={i.cans} unit={unit} onClick={onSkuClick ? () => onSkuClick(i.skuId) : undefined} />)}
      </div>

      {c && (
        <div className="flex flex-wrap gap-4 text-xs rounded-lg bg-paper border border-edge/70 p-2.5">
          <span className="text-muted">Planned <span className="text-ink font-medium tabular-nums">{num(planned)}</span></span>
          <span className="text-muted">Made <span className="text-ink font-medium tabular-nums">{num(c.items.reduce((a, i) => a + i.made, 0))}</span></span>
          <span className="text-muted">Sent to us <span className="text-ink font-medium tabular-nums">{num(c.items.reduce((a, i) => a + i.sent, 0))}</span></span>
          <span className="text-muted">Kept <span className="text-brand font-medium tabular-nums">{num(c.items.reduce((a, i) => a + i.kept, 0))}</span></span>
        </div>
      )}

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-3">
        <div className="text-[11px] text-muted order-2 sm:order-1">
          {stage === 0 && !dateConfirmed && (myTurn ? "Confirm the start date above (or suggest another) to get going." : `Start date proposed — waiting on ${them}.`)}
          {stage === 0 && dateConfirmed && "Confirm once all the ingredients for this run have arrived (check Raw Materials)."}
          {run.status === "Ingredients Received" && !allPackagingIn && `Tick off each packaging item above (${rc}/${K} in), then start production.`}
          {run.status === "Ingredients Received" && allPackagingIn && "Ingredients & packaging all in — start when you begin making it."}
          {run.status === "In Production" && "In production — record it when it's done."}
          {run.status === "Waiting Pickup" && "Produced. Kept added to your on-site stock. Ship the rest to us."}
          {run.status === "Delivered" && "✓ Shipped to Little Rick — this run is complete."}
        </div>
        <div className="flex flex-col sm:flex-row gap-2 order-1 sm:order-2 sm:shrink-0 [&>button]:w-full sm:[&>button]:w-auto [&>button]:whitespace-nowrap">
          {dateConfirmed && run.status !== "Waiting Pickup" && run.status !== "Delivered" && run.status !== "Cancelled" && <button className="btn-sm border border-warn/50 text-warn hover:bg-warn/5" disabled={busy} onClick={onDelay}>Report delay</button>}
          {stage === 0 && dateConfirmed && <button className="btn-primary btn-sm" disabled={busy} onClick={() => onSetStatus(run.id, "Ingredients Received")}>Ingredients received →</button>}
          {run.status === "Ingredients Received" && <>
            <button className="btn-sm border border-edge hover:bg-paper" disabled={busy} onClick={() => onSetStatus(run.id, "Booked")}>Undo</button>
            <button className="btn-primary btn-sm" disabled={busy || !allPackagingIn} title={allPackagingIn ? undefined : "Confirm all packaging first"} onClick={() => onSetStatus(run.id, "In Production")}>Start production →</button>
          </>}
          {/* Edit made/sent/kept at any stage once there's a recorded production. */}
          {c && (run.status === "In Production" || run.status === "Waiting Pickup" || run.status === "Delivered") && <button className="btn-sm border border-edge hover:bg-paper" disabled={busy} onClick={onRecord}>Edit amounts</button>}
          {run.status === "In Production" && !c && <button className="btn-primary btn-sm" disabled={busy} onClick={onRecord}>Record production complete →</button>}
          {run.status === "In Production" && c && <button className="btn-primary btn-sm" disabled={busy} onClick={() => onSetStatus(run.id, "Waiting Pickup")}>Mark produced →</button>}
          {run.status === "Waiting Pickup" && <button className="btn-primary btn-sm" disabled={busy} onClick={() => onSetStatus(run.id, "Delivered")}>Mark shipped to us →</button>}
        </div>
      </div>
    </div>
  );
}

// Brand-side shipment tracking for one packaging component (carrier / tracking / dates).
function ComponentTrackingModal({ name, shipment, busy, onClose, onSave }: {
  name: string; shipment: CompShipment; busy: boolean; onClose: () => void; onSave: (s: CompShipment) => void;
}) {
  const [carrier, setCarrier] = useState(shipment.carrier || "");
  const [trackingNumber, setTracking] = useState(shipment.trackingNumber || "");
  const [shippedDate, setShipped] = useState(shipment.shippedDate || "");
  const [expectedDelivery, setEta] = useState(shipment.expectedDelivery || "");
  const [note, setNote] = useState(shipment.note || "");
  const turl = trackingUrl(carrier, trackingNumber);
  return (
    <Modal title={`Tracking · ${name}`} onClose={onClose}>
      <div className="space-y-3">
        <p className="text-xs text-muted">Where this packaging is coming from — so the manufacturer sees when it's due and can mark it received on arrival.</p>
        <div className="grid grid-cols-2 gap-3">
          <LabeledInput label="Carrier" hint="UPS / FedEx / DHL / Royal Mail"><input className="input" value={carrier} onChange={(e) => setCarrier(e.target.value)} /></LabeledInput>
          <LabeledInput label="Tracking #"><input className="input" value={trackingNumber} onChange={(e) => setTracking(e.target.value)} /></LabeledInput>
          <LabeledInput label="Shipped date"><input type="date" className="input" value={shippedDate} onChange={(e) => setShipped(e.target.value)} /></LabeledInput>
          <LabeledInput label="Expected delivery"><input type="date" className="input" value={expectedDelivery} onChange={(e) => setEta(e.target.value)} /></LabeledInput>
        </div>
        <LabeledInput label="Note (optional)"><input className="input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. 2 pallets, arriving AM" /></LabeledInput>
        {turl && <a href={turl} target="_blank" rel="noreferrer" className="text-xs text-brand hover:underline">Open tracking ↗</a>}
        <div className="flex justify-end gap-2 pt-1">
          <button className="btn-ghost btn-sm" onClick={onClose}>Cancel</button>
          <button className="btn-primary btn-sm" disabled={busy} onClick={() => onSave({ carrier, trackingNumber, shippedDate, expectedDelivery, note })}>{busy ? "…" : "Save tracking"}</button>
        </div>
      </div>
    </Modal>
  );
}

// Responsive step tracker: a vertical timeline on phones (reads top-to-bottom, big touch targets)
// and the compact horizontal stepper on ≥sm.
function Timeline({ stage, steps, ingPct, ingredientDate, packPct }: { stage: number; steps: string[]; ingPct: number | null; ingredientDate?: string | null; packPct?: number | null }) {
  // Step 1 ("Ingredients in") and step 2 ("Packaging") show receiving progress as a partial % until done.
  const isPartial = (i: number) => (i === 1 && stage === 0 && ingPct != null && ingPct > 0 && ingPct < 100) || (i === 2 && packPct != null && packPct > 0 && packPct < 100);
  return (
    <>
      {/* Mobile: vertical timeline */}
      <ol className="sm:hidden">
        {steps.map((label, i) => {
          const done = i < stage, current = i === stage, last = i === steps.length - 1;
          return (
            <li key={label} className="flex gap-3">
              <div className="flex flex-col items-center">
                <div className={`w-6 h-6 rounded-full grid place-items-center text-[11px] font-semibold ${done ? "bg-ok text-white" : current ? "bg-brand text-white" : isPartial(i) ? "bg-warn text-white" : "bg-edge/60 text-muted"}`}>
                  {done ? "✓" : i + 1}
                </div>
                {!last && <div className={`w-0.5 flex-1 min-h-[14px] ${done ? "bg-ok" : "bg-edge/60"}`} />}
              </div>
              <div className="pb-3 min-w-0">
                <div className={`text-sm leading-6 ${current ? "text-ink font-semibold" : done ? "text-ink" : "text-muted"}`}>{label}</div>
                {i === 1 && ingPct != null && stage === 0 && <div className="text-[11px] font-semibold text-warn -mt-0.5">{ingPct}% ingredients in</div>}
                {i === 1 && ingredientDate && <div className="text-[11px] text-muted -mt-0.5">all in by {fmtDate(ingredientDate)}</div>}
                {i === 2 && packPct != null && <div className="text-[11px] font-semibold text-warn -mt-0.5">{packPct}% packaging in</div>}
              </div>
            </li>
          );
        })}
      </ol>
      {/* Desktop: horizontal stepper */}
      <div className="hidden sm:flex items-center">
        {steps.map((label, i) => (
          <div key={label} className="flex items-center flex-1 last:flex-none">
            <div className="flex flex-col items-center">
              <div className={`w-5 h-5 rounded-full grid place-items-center text-[10px] font-semibold ${i < stage ? "bg-ok text-white" : i === stage ? "bg-brand text-white" : isPartial(i) ? "bg-warn text-white" : "bg-edge/60 text-muted"}`}>
                {i < stage ? "✓" : i + 1}
              </div>
              <span className={`mt-1 text-[9px] text-center leading-tight w-14 ${i === stage ? "text-ink font-medium" : "text-muted"}`}>{label}</span>
              {i === 1 && ingPct != null && stage === 0 && <span className="text-[8px] font-semibold text-warn">{ingPct}%</span>}
              {i === 2 && packPct != null && <span className="text-[8px] font-semibold text-warn">{packPct}%</span>}
            </div>
            {i < steps.length - 1 && <div className={`flex-1 h-0.5 mx-1 -mt-4 ${i < stage ? "bg-ok" : "bg-edge/60"}`} />}
          </div>
        ))}
      </div>
    </>
  );
}

function CompletionModal({ run, skuName, unit, busy, onClose, onSave }: {
  run: Run; skuName: (id: string) => string; unit: string; busy: boolean;
  onClose: () => void; onSave: (items: CItem[], notes: string) => void;
}) {
  const [rows, setRows] = useState(() => run.items.map((it) => {
    const prev = run.completion?.items.find((c) => c.skuId === it.skuId);
    const made = prev?.made ?? it.cans;
    const sent = prev?.sent ?? made; // default: send everything, keep 0
    return { skuId: it.skuId, planned: it.cans, made: String(made), sent: String(sent) };
  }));
  const [notes, setNotes] = useState(run.completion?.notes || "");
  const set = (i: number, k: "made" | "sent", v: string) => setRows((s) => s.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));
  const kept = (r: { made: string; sent: string }) => Math.max(0, (Number(r.made) || 0) - (Number(r.sent) || 0));
  const totalPlanned = rows.reduce((a, r) => a + r.planned, 0);
  const totalMade = rows.reduce((a, r) => a + (Number(r.made) || 0), 0);
  const totalSent = rows.reduce((a, r) => a + (Number(r.sent) || 0), 0);
  const totalKept = rows.reduce((a, r) => a + kept(r), 0);

  return (
    <Modal title={`Record production · ${fmtDate(run.scheduledDate)}`} onClose={onClose} wide>
      <div className="space-y-3">
        <p className="text-xs text-muted"><span className="font-medium">Planned</span> is what we ordered (fixed). Enter what you <span className="font-medium">actually made</span> and how much you're <span className="font-medium">sending to us</span> — what's left is kept at your site and added to your At-Mfr stock.</p>
        {/* Mobile: one editable card per SKU */}
        <div className="sm:hidden space-y-3">
          {rows.map((r, i) => {
            const made = Number(r.made) || 0;
            const over = (Number(r.sent) || 0) > made;
            const short = made > 0 && made < r.planned;
            return (
              <div key={r.skuId} className="rounded-xl border border-edge/70 bg-paper p-3 space-y-2">
                <div className="font-medium text-sm">{skuName(r.skuId)}</div>
                <div className="grid grid-cols-2 gap-2 items-end">
                  <div><div className="label">Planned</div><div className="text-sm font-medium tabular-nums text-muted">{num(r.planned)}</div></div>
                  <div><div className="label">Keeping</div><div className="text-sm font-medium tabular-nums text-brand">{num(kept(r))}</div></div>
                  <label className="block"><span className="label">Made</span><input type="number" step="any" className={`input text-right ${short ? "border-warn" : ""}`} value={r.made} onChange={(e) => set(i, "made", e.target.value)} /></label>
                  <label className="block"><span className="label">Sending</span><input type="number" step="any" className={`input text-right ${over ? "border-danger" : ""}`} value={r.sent} onChange={(e) => set(i, "sent", e.target.value)} /></label>
                </div>
              </div>
            );
          })}
          <div className="rounded-xl border border-edge/70 p-3 grid grid-cols-2 gap-y-1 text-sm">
            <span className="text-muted">Total planned</span><span className="text-right tabular-nums">{num(totalPlanned)}</span>
            <span className="text-muted">Total made</span><span className={`text-right tabular-nums font-medium ${totalMade < totalPlanned ? "text-warn" : ""}`}>{num(totalMade)}</span>
            <span className="text-muted">Total sending</span><span className="text-right tabular-nums font-medium">{num(totalSent)}</span>
            <span className="text-muted">Total keeping</span><span className="text-right tabular-nums font-medium text-brand">{num(totalKept)}</span>
          </div>
        </div>
        {/* Desktop: table grid */}
        <div className="hidden sm:grid grid-cols-[1fr_auto_auto_auto_auto] gap-x-3 gap-y-2 items-center text-sm">
          <div className="text-[11px] uppercase tracking-wide text-muted">SKU</div>
          <div className="text-[11px] uppercase tracking-wide text-muted text-right w-20">Planned</div>
          <div className="text-[11px] uppercase tracking-wide text-muted text-right w-20">Made</div>
          <div className="text-[11px] uppercase tracking-wide text-muted text-right w-20">Sending</div>
          <div className="text-[11px] uppercase tracking-wide text-muted text-right w-20">Keeping</div>
          {rows.map((r, i) => {
            const made = Number(r.made) || 0;
            const over = (Number(r.sent) || 0) > made;
            const short = made > 0 && made < r.planned;
            return (
              <div key={r.skuId} className="contents">
                <div className="font-medium truncate">{skuName(r.skuId)}</div>
                <div className="text-right tabular-nums w-20 text-muted">{num(r.planned)}</div>
                <input type="number" step="any" className={`input py-1 text-right w-20 ${short ? "border-warn" : ""}`} title={short ? `${num(r.planned - made)} under plan` : undefined} value={r.made} onChange={(e) => set(i, "made", e.target.value)} />
                <input type="number" step="any" className={`input py-1 text-right w-20 ${over ? "border-danger" : ""}`} value={r.sent} onChange={(e) => set(i, "sent", e.target.value)} />
                <div className="text-right tabular-nums w-20 text-brand">{num(kept(r))}</div>
              </div>
            );
          })}
          {/* totals */}
          <div className="text-[11px] font-semibold text-muted pt-1 border-t border-edge/60">Total</div>
          <div className="text-right tabular-nums w-20 pt-1 border-t border-edge/60 text-muted">{num(totalPlanned)}</div>
          <div className={`text-right tabular-nums w-20 pt-1 border-t border-edge/60 font-medium ${totalMade < totalPlanned ? "text-warn" : ""}`}>{num(totalMade)}</div>
          <div className="text-right tabular-nums w-20 pt-1 border-t border-edge/60 font-medium">{num(totalSent)}</div>
          <div className="text-right tabular-nums w-20 pt-1 border-t border-edge/60 font-medium text-brand">{num(totalKept)}</div>
        </div>
        <div className="text-xs text-muted text-right"><span className="font-medium text-brand">{num(totalKept)} {unit}s</span> kept → added to your At-Mfr stock.</div>
        <LabeledInput label="Notes (optional)"><textarea className="input" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Batch/lot, yield notes, why the yield was under plan…" /></LabeledInput>
        <div className="flex justify-end gap-2 pt-1">
          <button className="btn-ghost btn-sm" onClick={onClose}>Cancel</button>
          <button className="btn-primary btn-sm" disabled={busy} onClick={() => onSave(rows.map((r) => ({ skuId: r.skuId, made: Number(r.made) || 0, sent: Number(r.sent) || 0, kept: kept(r) })), notes)}>{busy ? "…" : "Save production"}</button>
        </div>
      </div>
    </Modal>
  );
}

function RunDetailsModal({ run, busy, onClose, onSave }: {
  run: Run; busy: boolean; onClose: () => void; onSave: (patch: RunPatch) => void;
}) {
  const [scheduledDate, setSched] = useState(run.scheduledDate || "");
  const [readyDate, setReady] = useState(run.readyDate || "");
  const [poNumber, setPo] = useState(run.poNumber || "");
  const [batchNumber, setBatch] = useState(run.batchNumber || "");
  const [notes, setNotes] = useState(run.notes || "");
  return (
    <Modal title="Production details" onClose={onClose}>
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <LabeledInput label="Start date" hint="When production is scheduled to run"><input type="date" className="input" value={scheduledDate} onChange={(e) => setSched(e.target.value)} /></LabeledInput>
          <LabeledInput label="Ready date" hint="When it'll be ready — push this out to log a delay"><input type="date" className="input" value={readyDate} onChange={(e) => setReady(e.target.value)} /></LabeledInput>
          <LabeledInput label="PO / tolling #"><input className="input" value={poNumber} onChange={(e) => setPo(e.target.value)} /></LabeledInput>
          <LabeledInput label="Batch / lot #"><input className="input" value={batchNumber} onChange={(e) => setBatch(e.target.value)} /></LabeledInput>
        </div>
        <LabeledInput label="Notes" hint="Delay reasons, anything the team should know"><textarea className="input" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. delayed 1 week — waiting on CBD emulsion" /></LabeledInput>
        <div className="flex justify-end gap-2 pt-1">
          <button className="btn-ghost btn-sm" onClick={onClose}>Cancel</button>
          <button className="btn-primary btn-sm" disabled={busy} onClick={() => onSave({ scheduledDate, readyDate, poNumber, batchNumber, notes })}>{busy ? "…" : "Save"}</button>
        </div>
      </div>
    </Modal>
  );
}

function ScheduleModal({ run, role, busy, onClose, onSave }: {
  run: Run; role: "owner" | "mfr"; busy: boolean; onClose: () => void; onSave: (date: string, note: string) => void;
}) {
  const [date, setDate] = useState(run.scheduledDate || "");
  const [note, setNote] = useState("");
  const them = role === "owner" ? "the manufacturer" : "Little Rick";
  return (
    <Modal title="Suggest another date" onClose={onClose}>
      <div className="space-y-3">
        <p className="text-xs text-muted">Propose a different start date and (optionally) say why. This goes back to {them} to confirm or counter — the current proposal was {fmtDate(run.scheduledDate)}.</p>
        <LabeledInput label="Proposed start date"><input type="date" className="input" value={date} onChange={(e) => setDate(e.target.value)} /></LabeledInput>
        <LabeledInput label="Note (optional)"><textarea className="input" rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. line's tied up that week — the 21st works better" /></LabeledInput>
        <div className="flex justify-end gap-2 pt-1">
          <button className="btn-ghost btn-sm" onClick={onClose}>Cancel</button>
          <button className="btn-primary btn-sm" disabled={busy || !date} onClick={() => onSave(date, note)}>{busy ? "…" : "Send proposal"}</button>
        </div>
      </div>
    </Modal>
  );
}

// Brand-only "remind the manufacturer" composer. Pre-fills an editable message summarising what's
// outstanding for THIS run (confirm deliveries, mark packaging, start/ship, update stock), then sends
// it as a manufacturer notification (which also emails their configured contacts).
function NudgeModal({ run, comps, ing, busy, onClose, onSend }: {
  run: Run; comps: string[]; ing: { have: number; total: number; missing: string[] } | null; busy: boolean;
  onClose: () => void; onSend: (title: string, message: string) => Promise<{ emailed: boolean; recipients: number } | void>;
}) {
  const stage = stageOf(run);
  const sched = run.schedule;
  const awaitingMfr = !!sched && sched.state !== "confirmed" && sched.awaiting === "mfr";
  const compList = comps.length ? comps : [];
  const receivedSet = new Set(run.componentsReceived?.length ? run.componentsReceived : (run.packagingReceived ? compList : []));
  const pendingComps = compList.filter((cn) => !receivedSet.has(cn));

  const suggest = () => {
    const L: string[] = [`Hi — could you please update the production tool for the ${fmtDate(run.scheduledDate)} run:`, ""];
    if (awaitingMfr) L.push(`• Confirm the proposed start date of ${fmtDate(run.scheduledDate)} (or suggest another).`);
    if (stage <= 1) L.push("• Confirm whether the ingredients / fruit purées for this run have arrived — plus any other items listed (corks, labels, etc.).");
    if (run.status === "Ingredients Received" && pendingComps.length) L.push(`• Mark the packaging received once it's in: ${pendingComps.join(", ")}.`);
    if (run.status === "Ingredients Received" && !pendingComps.length) L.push("• Ingredients & packaging are all in — please start production when you can.");
    if (run.status === "In Production" && !run.completion) L.push("• Record what you made once production is complete.");
    if (run.status === "Waiting Pickup") L.push("• Ship the produced stock to us.");
    if (ing && ing.missing.length) L.push(`• Still showing short on: ${ing.missing.slice(0, 8).join(", ")} — please confirm/receive these.`);
    L.push("• Make sure the ingredient inventory reflects your current stock levels.", "", "Thanks!");
    return L.join("\n");
  };

  const [title, setTitle] = useState(`Reminder · ${fmtDate(run.scheduledDate)} production`);
  const [message, setMessage] = useState(suggest());
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState<{ emailed: boolean; recipients: number } | null>(null);
  const send = async () => {
    if (!message.trim()) return;
    setSending(true);
    try { const r = await onSend(title.trim(), message.trim()); setSent(r && typeof r === "object" ? r : { emailed: false, recipients: 0 }); }
    finally { setSending(false); }
  };

  return (
    <Modal title="Remind the manufacturer" onClose={onClose}>
      {sent ? (
        <div className="space-y-3">
          <div className="text-sm font-medium text-ok">✓ Reminder sent.</div>
          <p className="text-xs text-muted">{sent.emailed
            ? `Emailed to ${sent.recipients} manufacturer contact${sent.recipients !== 1 ? "s" : ""} and posted to their portal bell.`
            : "Posted to their portal bell. To also email them, add manufacturer addresses in Admin → Business → Notifications."}</p>
          <div className="flex justify-end"><button className="btn-primary btn-sm" onClick={onClose}>Done</button></div>
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-xs text-muted">Sends to the manufacturer's portal and emails their configured contacts. It's pre-filled from what's outstanding on this run — edit freely.</p>
          <LabeledInput label="Subject"><input className="input" value={title} onChange={(e) => setTitle(e.target.value)} /></LabeledInput>
          <LabeledInput label="Message"><textarea className="input min-h-[200px] leading-relaxed" value={message} onChange={(e) => setMessage(e.target.value)} /></LabeledInput>
          <div className="flex justify-end gap-2 pt-1">
            <button className="btn-ghost btn-sm" onClick={onClose}>Cancel</button>
            <button className="btn-primary btn-sm" disabled={sending || busy || !message.trim()} onClick={send}>{sending ? "Sending…" : "Send reminder"}</button>
          </div>
        </div>
      )}
    </Modal>
  );
}

function DelayModal({ run, busy, onClose, onSave }: {
  run: Run; busy: boolean; onClose: () => void; onSave: (toDate: string, reason: string) => void;
}) {
  const [toDate, setTo] = useState(run.readyDate || run.scheduledDate || "");
  const [reason, setReason] = useState("");
  const slip = daysBetween(run.readyDate || run.scheduledDate, toDate);
  return (
    <Modal title="Report a delay" onClose={onClose}>
      <div className="space-y-3">
        <p className="text-xs text-muted">Push the ready date out and say why. The original date is kept on record{run.originalReadyDate ? ` (originally ready ~${fmtDate(run.originalReadyDate)})` : ""}, so the delay is logged rather than hidden.</p>
        <LabeledInput label="New ready date"><input type="date" className="input" value={toDate} onChange={(e) => setTo(e.target.value)} /></LabeledInput>
        {slip !== 0 && <div className={`text-xs ${slip > 0 ? "text-danger" : "text-muted"}`}>{slip > 0 ? `+${slip} days later` : `${-slip} days earlier`}{run.readyDate ? ` than the current ready date (~${fmtDate(run.readyDate)})` : ""}</div>}
        <LabeledInput label="Reason"><textarea className="input" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. waiting on CBD emulsion delivery" /></LabeledInput>
        <div className="flex justify-end gap-2 pt-1">
          <button className="btn-ghost btn-sm" onClick={onClose}>Cancel</button>
          <button className="btn-primary btn-sm" disabled={busy || !toDate} onClick={() => onSave(toDate, reason)}>{busy ? "…" : "Log delay"}</button>
        </div>
      </div>
    </Modal>
  );
}
